import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ProductHardwareItem, ProductHardwareItemInsert } from '../lib/database.types'

const HW_KEY = (productId: string) => ['product_hardware_items', productId]

export function useProductHardware(productId: string | undefined) {
  return useQuery({
    queryKey: HW_KEY(productId ?? ''),
    enabled: !!productId,
    queryFn: async (): Promise<ProductHardwareItem[]> => {
      const { data, error } = await supabase
        .from('product_hardware_items')
        .select('*')
        .eq('product_id', productId!)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useReplaceProductHardware(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (items: Array<Omit<ProductHardwareItemInsert, 'user_id' | 'product_id'>>): Promise<void> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      // Delete existing and re-insert (simplest sync semantics for a form).
      // Throw on failure or the insert below races against stale rows that
      // would re-cause RLS conflicts and produce a partial save.
      const { error: delErr } = await supabase
        .from('product_hardware_items')
        .delete()
        .eq('product_id', productId)
      if (delErr) throw delErr
      if (items.length > 0) {
        const rows = items.map((it, i) => ({
          ...it,
          user_id: user.id,
          product_id: productId,
          sort_order: i,
        }))
        const { error } = await supabase.from('product_hardware_items').insert(rows)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HW_KEY(productId) })
    },
  })
}
